import { MatDialog } from '@angular/material/dialog'
import { BranchNameType, BusinessObjectItem } from '@lg/shared'
import { Screen } from '@lg/shared'
import { Observable, Subscription, of } from 'rxjs'
import {
	Component,
	OnInit,
	Input,
	OnDestroy,
	OnChanges,
	NgZone,
	QueryList,
	ViewChildren,
	AfterViewInit,
	ContentChildren,
	TemplateRef,
	HostListener,
	signal,
	ViewChild,
	inject,
	input,
	resource,
	computed,
} from '@angular/core'
import { EditorPropertiesManager, ItemAndCacheKey } from '@lg/shared'
import { Block, TextBlock } from '@lg/shared'
import { FormGroup, FormControl, FormArray } from '@angular/forms'
import type { ScreenEditorComponent } from '../screen-editor/screen-editor.component'
import { BoReference } from '@lg/shared'
import { ActivatedRoute } from '@angular/router'
import {
	EditorCodeProperty,
	EditorDropdownProperty,
	EditorPropertyWithKey,
	EditorInputProperty,
	EditorTsCodeProperty,
	EditorWizardData,
	EditorWizardDialog,
	EditorBoRefDetailProperty,
	EditorCompositionProperty,
	EditorBoRefProperty,
} from '@lg/shared'
import { map, shareReplay, tap } from 'rxjs/operators'
import { openDialogAndGetPromise } from '../../util/ui-util'
import type { ProcessEditorComponent } from '../process-editor/process-editor.component'
import { BusinessObject } from '@lg/shared'
import { DynamicTabDirective } from '@lg/ng-shared'
import {
	FieldsetPropertyBlock,
	GapPropertyBlock,
	PropertyBlock,
	PropertyPropertyBlock,
} from './property-blocks'
import { ContentAreaBlock } from '@lg/shared'
import { StudioTrpcService } from '@lg/ng-shared'
import { MatTabGroup } from '@angular/material/tabs'
import { Semaphore } from '@lg/shared'
import { TextOrCode } from '@lg/shared'
import { BoSpecificDetails } from '@lg/shared'
import { EditorHelperServiceInjectionToken } from '../../injection-tokens'
import { path } from 'ramda'

@Component({
	selector: 'app-properties',
	templateUrl: './properties.component.html',
	styleUrls: ['./properties.component.scss'],
	standalone: false,
})
export class PropertiesComponent
	implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
	itemsAndCacheKeys_input = input.required<ItemAndCacheKey[]>({
		alias: 'itemsAndCacheKeys',
	})
	itemsAndCacheKeys = computed(() => this.itemsAndCacheKeys_input(), {
		equal: (a, b) =>
			a.length === b.length &&
			a.every(
				(itemAndCacheKey, index) =>
					itemAndCacheKey.cacheKey === b[index].cacheKey,
			),
	})
	items = computed(() =>
		this.itemsAndCacheKeys().map((itemAndCacheKey) => itemAndCacheKey.item),
	)
	branchName = input.required<BranchNameType>()
	bo = input.required<BusinessObject>()
	typeDeclarations = input.required<string>()
	editor = input.required<ScreenEditorComponent | ProcessEditorComponent>()
	propertiesForm = resource({
		params: () => ({
			itemsAndCacheKeys: this.itemsAndCacheKeys(),
		}),
		loader: async ({ params }) => {
			const { itemsAndCacheKeys } = params
			const lock = await this.propertiesUpdateSemaphore.obtainLock()
			try {
				if (this.formSubscription) this.formSubscription.unsubscribe()

				const item0 = itemsAndCacheKeys[0].item
				const propertyControls: Record<string | number | symbol, FormControl> =
					{}
				this.textOrCodeProperties.clear()
				this.propertyOptionsCache = new WeakMap()

				const itemProperties = itemsAndCacheKeys.map((itemAndCacheKey) => [
					...EditorPropertiesManager.getAllProperties(itemAndCacheKey),
					...(itemAndCacheKey.item.getAdditionalEditorProperties?.() ?? []),
				])

				this.properties = []
				for (const property of itemProperties[0]) {
					const propertyForAllItems = itemProperties.map((props) =>
						props.find((prop) => prop.key == property.key),
					)
					if (propertyForAllItems.every(Boolean)) {
						// all items have this property; therefore show for editing; otherwise omit
						this.properties.push(property)
					} else {
						continue
					}
				}

				this.wizardDialogs = this.helperService.getWizardDialogs(item0?.__type)
				const helper = await this.helperService.getHelper(
					item0?.__type || item0?.type,
				)
				await helper?.modifyEditorProperties(
					item0,
					this.properties,
					this.propertiesForm.value()?.value ?? {},
				)
				this.updateDisplayedProperties()

				for (const property of this.effectiveProperties) {
					let value = property.getter(item0, property.key)
					const keyParts = property.key.split('#')
					const rawValue = path(keyParts, item0)
					if (rawValue instanceof TextOrCode)
						this.textOrCodeProperties.set(property.key, rawValue)

					propertyControls[property.key] = new FormControl(value)
					if (
						property.isReadonly?.(
							this.items(),
							this.effectiveProperties,
							this.bo(),
						)
					) {
						propertyControls[property.key].disable()
					}

					if (property.inputType == 'boRef') {
						this.boRefs$[property.key] = this.trpcService
							.queryAsObservable((client) =>
								client.bo.getBoReferences.query({
									branchName: this.activatedRoute.snapshot.params.branchName,
									moduleId: this.activatedRoute.snapshot.params.moduleId,
									boTypes: property.allowedBoTypes,
									includeImportedModules: property.includeImportedModules,
								}),
							)
							.pipe(
								map((boRefs) => boRefs.map((boRef) => new BoReference(boRef))),
								shareReplay(1),
							)
					}

					if (property.inputType == 'boRefDetail') {
						const boRef = (
							property as EditorBoRefDetailProperty<any, any>
						).boRef(this.items(), this.effectiveProperties, this.bo())

						if (boRef?.isFullyDefined()) {
							this.boRefDetails$[property.key] = this.trpcService
								.queryAsObservable((client) =>
									client.bo.getBoSpecificDetails.query({
										branchName: this.activatedRoute.snapshot.params.branchName,
										...boRef,
									}),
								)
								.pipe(
									map((details) => {
										const detail = details[property.detailName]
										if (!detail) return []
										if (Array.isArray(detail))
											return detail.map(
												(name) => [name, name] as [string, string],
											)
										return Object.entries(detail)
									}),
									shareReplay(1),
								)
						} else {
							this.boRefDetails$[property.key] = of([])
						}
					}
				}

				const propertiesForm = new FormGroup(propertyControls)
				this.formSubscription = propertiesForm.valueChanges.subscribe(() => {
					const formValues = propertiesForm.getRawValue() // do NOT use formValues provided by valueChanges event as this would not include values for disabled controls

					for (const property of this.effectiveProperties) {
						const oldValue = property.getter(item0, property.key)
						const value = Reflect.get(formValues, property.key)

						if (value === oldValue) continue
						for (const item of this.items()) {
							property.setter(item, property.key, value)
						}
					}
				})
				return propertiesForm
			} finally {
				lock.release()
			}
		},
		defaultValue: null,
	})
	textOrCodeProperties = new Map<string, TextOrCode>()
	formSubscription: Subscription
	properties: EditorPropertyWithKey<BusinessObject, any>[] = []
	effectiveProperties: EditorPropertyWithKey<BusinessObject, any>[] = []
	tabNames = new Set<string>()
	tabPropertyBlocks: Record<string, PropertyBlock[]> = {}
	wizardDialogs: EditorWizardDialog[] = []
	areValuesMatching = new Map<string, boolean>()
	propertyOptionsCache = new WeakMap<
		EditorPropertyWithKey<BusinessObject, any>,
		Promise<[any, string][]>
	>()
	@ContentChildren(DynamicTabDirective)
	additionalTabs!: QueryList<DynamicTabDirective>
	@ViewChild(MatTabGroup, { static: true }) tabGroup: MatTabGroup

	boRefs$: Record<string, Observable<BoReference[]>> = {}
	boRefDetails$: Record<string, Observable<[string, string][]>> = {}
	bs = signal<Block[]>([])
	private propertiesUpdateSemaphore = new Semaphore()
	private helperService = inject(EditorHelperServiceInjectionToken)

	constructor(
		private trpcService: StudioTrpcService,
		private activatedRoute: ActivatedRoute,
		private dialog: MatDialog,
		private ngZone: NgZone,
	) {}

	async ngOnInit() {
		for (const property of this.effectiveProperties) {
			property.onInitializedOrChanged?.(
				this.items(),
				this.effectiveProperties,
				this.bo(),
			)
		}
	}

	ngAfterViewInit(): void {
		setTimeout(() => (this.tabGroup.selectedIndex = 0))
	}

	async onPropertyChanged(
		property: EditorPropertyWithKey<BusinessObject, any>,
		updateForm: boolean,
	) {
		for (const block of this.items()) {
			const helper = await this.helperService.getHelper(block.__type)
			await helper?.onPropertyChanged(block, property, this.effectiveProperties)
		}

		property.onChanged?.(this.items(), this.effectiveProperties, this.bo())
		property.onInitializedOrChanged?.(
			this.items(),
			this.effectiveProperties,
			this.bo(),
		)
		if (updateForm) {
			this.propertiesForm.reload()
		}
	}

	getCodeContext$(property: EditorPropertyWithKey<BusinessObject, any>) {
		const additionalVariables = property.additionalVariables?.(
			this.items(),
			this.effectiveProperties,
			this.bo(),
		)
		const additionalLines = property.additionalLines?.(
			this.items(),
			this.effectiveProperties,
			this.bo(),
		)
		return this.editor().getCodeContext$(
			'editorProperty',
			property,
			undefined,
			additionalVariables,
			additionalLines,
		)
	}

	ngOnChanges(changes) {
		this.ngOnInit()
	}

	ngOnDestroy() {
		this.formSubscription.unsubscribe()
	}

	stopPropagation(event: Event) {
		event.stopPropagation()
	}

	matchBoRefs(ref1: BoReference, ref2: BoReference) {
		return ref1?.matches(ref2) ?? false
	}

	async openWizardDialog(event: MouseEvent, wizard: EditorWizardDialog) {
		event.preventDefault()
		if (wizard.getDialogClass) {
			console.log('blocks', this.items())
			const block = await openDialogAndGetPromise<
				Block,
				EditorWizardData<Screen, Block>
			>(this.dialog, wizard.getDialogClass(), {
				data: {
					branchName: this.branchName(),
					bo: this.bo() as Screen,
					item: this.items()[0] as Block,
				},
			})
			if (block) {
				const replaced = (
					this.editor() as ScreenEditorComponent
				).bo.replaceBlock(this.items()[0] as Block, block)
				this.items().splice(0, 1, block)

				// console.log('replaced', replaced, block)
			} else {
				this.propertiesForm.reload()
				// console.log('closed without replacement')
			}
		} else {
			console.error(`No dialog class defined for wizard ${wizard.label}`)
		}
	}

	isTextOrCode(value: any): value is TextOrCode {
		return value instanceof TextOrCode
	}
	asInputProperty(property: EditorPropertyWithKey<BusinessObject, any>) {
		return property as EditorInputProperty<Screen, Block>
	}
	asTsCodeProperty(property: EditorPropertyWithKey<BusinessObject, any>) {
		return property as EditorTsCodeProperty<Screen, Block>
	}
	asDropdownProperty(property: EditorPropertyWithKey<BusinessObject, any>) {
		return property as EditorDropdownProperty<Screen, Block>
	}
	asDropdownPropertyOptions(
		property: EditorPropertyWithKey<BusinessObject, any>,
	) {
		if (!this.propertyOptionsCache.has(property)) {
			this.propertyOptionsCache.set(
				property,
				new Promise(async (res, rej) => {
					try {
						let options = this.asDropdownProperty(property)?.options
						if (options instanceof Function) {
							options = await options(
								this.items() as Block[],
								this.effectiveProperties,
								this.bo() as Screen,
							)
						}
						res(options)
						// console.log('options', options)
					} catch (err) {
						rej(err)
					}
				}),
			)
		}
		return this.propertyOptionsCache.get(property)
	}
	asBoRefProperty(property: EditorPropertyWithKey<BusinessObject, any>) {
		return property as EditorBoRefProperty<Screen, Block>
	}
	asBoRefDetailProperty(property: EditorPropertyWithKey<BusinessObject, any>) {
		return property as EditorBoRefDetailProperty<Screen, Block>
	}
	entriesOf<T extends object>(obj: T) {
		if (!obj) return []
		return Object.entries(obj)
	}

	getTsReturnType(property: EditorPropertyWithKey<BusinessObject, any>) {
		return this.asTsCodeProperty(property).tsReturnType?.(
			this.items() as Block[],
			this.bo() as Screen,
		)
	}

	getMultiSelectionValue(
		property: EditorPropertyWithKey<BusinessObject, any>,
		which: 'first' | 'last',
	) {
		const item = this.items().at(which == 'first' ? 0 : -1)
		return property.getter(item, property.key)
	}

	setMultiSelectionValue(
		property: EditorPropertyWithKey<BusinessObject, any>,
		which: 'first' | 'last',
	) {
		const value = this.getMultiSelectionValue(property, which)
		for (const item of this.items()) {
			property.setter(item, property.key, value)
		}
		this.areValuesMatching.set(property.key, true)
		this.propertiesForm.value().controls[property.key].setValue(value)
	}

	cycleTextOrCodeType(textOrCode: TextOrCode) {
		const allowedTypes = textOrCode.getAllowedTypes()
		const currentIndex = allowedTypes.indexOf(textOrCode.type)
		const newIndex = (currentIndex + 1) % allowedTypes.length
		textOrCode.type = allowedTypes[newIndex]
	}

	updateDisplayedProperties() {
		this.effectiveProperties = this.properties.flatMap((p) => {
			const compositionObjFn = (p as EditorCompositionProperty<any, any>)
				.compositionObj
			if (compositionObjFn) {
				const subItem = compositionObjFn(
					this.items(),
					this.effectiveProperties,
					this.bo(),
				)
				const subItemAndCacheKey = {
					item: subItem,
					cacheKey: subItem,
				}
				const subProperties = EditorPropertiesManager.getAllProperties(
					subItemAndCacheKey,
				).map((subProperty) => ({
					...subProperty,
					key: `${p.key}#${subProperty.key}`,
					order: p.order + subProperty.order / 10000,
					setter: (target: object, key: string, value: any) => {
						const subObj = target[p.key]
						const subSetter = subProperty.setter
						subSetter(subObj, subProperty.key, value)
					},
					getter: (target, key) => {
						const subObj = target[p.key]
						const subGetter = subProperty.getter
						return subGetter(subObj, subProperty.key)
					},
				}))
				return subProperties
			} else {
				return [p]
			}
		})
		this.effectiveProperties.sort((a, b) => a.order - b.order)

		this.areValuesMatching = new Map()
		for (const property of this.effectiveProperties) {
			const itemValues = this.items().map((item) =>
				JSON.stringify(property.getter(item, property.key)),
			)
			this.areValuesMatching.set(property.key, new Set(itemValues).size == 1)
		}

		const blocksToVisibleBlocks = (
			blocks: PropertyBlock[],
		): PropertyBlock[] => {
			return blocks.flatMap((block) => {
				if (block.type == 'gap') return []
				if (block.type == 'fieldset') {
					return [
						{
							...block,
							propertyBlocks: blocksToVisibleBlocks(block.propertyBlocks),
						} as PropertyBlock,
					]
				}

				const p = block.property
				const isVisible =
					!p.isVisible ||
					p.isVisible(this.items(), this.effectiveProperties, this.bo())
				if (isVisible) {
					return [block]
				}

				// if gap should be shown instead of completely hiding, add a placeholder for the gap
				const leaveGapIfHidden = p.leaveGapIfHidden?.(
					this.items(),
					this.effectiveProperties,
					this.bo(),
				)
				if (leaveGapIfHidden) {
					return [
						{
							type: 'gap',
						} as PropertyBlock,
					]
				}

				// otherwise do not add a property
				return []
			})
		}

		this.tabNames = new Set(this.effectiveProperties.map((p) => p.tab))

		this.tabPropertyBlocks = Object.fromEntries(
			[...this.tabNames].map((tab) => {
				const tabProperties = this.effectiveProperties.filter(
					(p) => p.tab == tab,
				)
				const fieldsets = new Map<string, FieldsetPropertyBlock>()
				const propertyBlocks: PropertyBlock[] = []

				for (const p of tabProperties) {
					if (p.fieldset) {
						const fieldsetName = p.fieldset(
							this.items(),
							this.effectiveProperties,
							this.bo(),
						)
						let fieldset = fieldsets.get(fieldsetName)
						if (fieldset) {
							// fieldset already created & added; just add property to it but don't add a block to the main list
							fieldset.propertyBlocks.push({
								type: 'property',
								property: p,
							} as PropertyPropertyBlock)
						} else {
							// create fieldset with current property and add it to list
							fieldset = {
								type: 'fieldset',
								propertyBlocks: [
									{ type: 'property', property: p } as PropertyPropertyBlock,
								],
								legend: fieldsetName,
							}
							fieldsets.set(fieldsetName, fieldset)
							propertyBlocks.push(fieldset)
						}
					} else {
						propertyBlocks.push({
							type: 'property',
							property: p,
						} as PropertyPropertyBlock)
					}
				}

				const visibleTabBlocks = blocksToVisibleBlocks(propertyBlocks)

				return [tab, visibleTabBlocks]
			}),
		)

		this.tabNames = new Set(
			[...this.tabNames].filter((tab) => this.tabPropertyBlocks[tab]?.length),
		)
	}

	getLabel(property: EditorPropertyWithKey<BusinessObject, any>) {
		if (typeof property.label == 'function') {
			return property.label(
				this.items() as Block[],
				this.effectiveProperties,
				this.bo(),
			)
		}
		return property.label
	}

	asPropertyBlock(block: unknown) {
		return block as PropertyBlock
	}

	asPropertyPropertyBlock(block: unknown) {
		return block as PropertyPropertyBlock
	}

	asFieldsetPropertyBlock(block: unknown) {
		return block as FieldsetPropertyBlock
	}

	isBlock(item: BusinessObjectItem): item is Block {
		return item instanceof Block
	}

	addContentAreaForProperty(
		property: EditorTsCodeProperty<Screen, Block> &
			EditorPropertyWithKey<Screen, Block>,
	) {
		if (
			!confirm(
				`Move property "${property.label}" into ContentArea "${property.alternativeContentAreaName}"?`,
			)
		)
			return

		for (const block of this.items() as Block[]) {
			let code: string
			code = property.getter(block, property.key)
			let rawCode = Reflect.get(block, property.key)
			if (rawCode instanceof TextOrCode) {
				// clone to avoid changing the original object
				const original = rawCode as any
				rawCode = { ...rawCode }
				rawCode.__proto__ = original.__proto__
			}

			property.setter(block, property.key, '')

			let contentArea = block.getContentArea(
				property.alternativeContentAreaName,
			)
			if (!contentArea) {
				contentArea = new ContentAreaBlock({
					name: property.alternativeContentAreaName,
				})
				block.children.splice(0, 0, contentArea)
			}

			let textBlock = contentArea.children.find(
				(child) => child instanceof TextBlock,
			) as TextBlock
			if (!textBlock) {
				textBlock = new TextBlock()
				contentArea.children.splice(0, 0, textBlock)
			}
			if (rawCode instanceof TextOrCode) {
				textBlock.textCode = rawCode
			} else {
				textBlock.textCode.set(code, 'E')
			}
		}

		this.propertiesForm.reload()
	}

	removeContentAreaForProperty(
		property: EditorTsCodeProperty<Screen, Block> &
			EditorPropertyWithKey<Screen, Block>,
	) {
		if (
			!confirm(
				`Move ContentArea "${property.alternativeContentAreaName}" into property "${property.label}"?`,
			)
		)
			return

		for (const block of this.items() as Block[]) {
			let contentArea = block.getContentArea(
				property.alternativeContentAreaName,
			)

			if (contentArea) {
				const textBlock =
					contentArea.children.find((b) => b instanceof TextBlock) ??
					(new TextBlock() as TextBlock)

				const rawCode = Reflect.get(block, property.key)
				if (rawCode instanceof TextOrCode) {
					rawCode.set(textBlock.textCode.content, textBlock.textCode.type)
				} else {
					property.setter(
						block,
						property.key,
						textBlock.textCode.getAsExpressionOrDefault(''),
					)
				}

				block.children = block.children.filter((b) => b !== contentArea)
			}
		}

		this.propertiesForm.reload()
	}

	@HostListener('cut', ['$event'])
	@HostListener('copy', ['$event'])
	@HostListener('paste', ['$event'])
	preventBubbling(event: Event) {
		// stop propagation as otherwise cut, copy and paste events would be fired on the selected blocks
		if (document.activeElement !== document.body) {
			event.stopPropagation()
		}
	}
}
